import { z } from "zod";
import { useCallback } from "react";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card } from "react-bootstrap";
import { defineMessages, FormattedMessage, IntlShape, useIntl } from "react-intl";
import ContentWrapper from "../../components/content-wrapper";
import { FunctionalityPeriod } from "./components/Period";
import { FunctionalityFilter } from "./components/Filter";
import { FunctionalityGroup } from "./components/Group";
import { SectionHeading } from "../../components/section-heading";
import { useConfig } from "../../lib/config";
import { executeSuccessLogic } from "./logic";
import { jsonToXlsx } from "./infrastructure/xlsx";
import { AuthorizationVerifier } from "../../components/authorization-verifier";

const messages = defineMessages({
  title: {
    id: "functionalityReport.title",
    defaultMessage: "Functionality Report",
  },
  processing: {
    id: "functionalityReport.processing",
    defaultMessage: "Processing... This may take a while",
  },
  export: {
    id: "functionalityReport.export",
    defaultMessage: "Export",
  },
  errorRequiredFunctionalities: {
    id: "functionalityReport.errorRequiredFunctionalities",
    defaultMessage: "Select at least one functionality",
  },
  errorRequiredStartDate: {
    id: "functionalityReport.errorRequiredStartDate",
    defaultMessage: "Start date is required",
  },
  errorRequiredEndDate: {
    id: "functionalityReport.errorRequiredEndDate",
    defaultMessage: "End date is required",
  },
  errorAtLeastOneGroup: {
    id: "functionalityReport.errorAtLeastOneGroup",
    defaultMessage: "Add at least one group",
  },
  errorAtLeastOneHotelTicker: {
    id: "functionalityReport.errorAtLeastOneHotelTicker",
    defaultMessage: "Select at least one hotel ticker",
  },
  errorGroupNameMinLength: {
    id: "functionalityReport.errorGroupNameMinLength",
    defaultMessage: "Group name must be at least {min} characters",
  },
});

const baseSchema = z.object({
  functionalities: z.array(z.string()).min(1),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  groups: z
    .array(
      z.object({
        name: z.string().min(3).optional(),
        hotelTickers: z.array(z.string()).min(1),
      })
    )
    .min(1),
});

function makeSchema(intl: IntlShape) {
  return baseSchema.extend({
    functionalities: z
      .array(z.string())
      .min(1, intl.formatMessage(messages.errorRequiredFunctionalities)),
    startDate: z.string({
      error: issue =>
        issue.input === undefined
          ? intl.formatMessage(messages.errorRequiredStartDate)
          : "Invalid value",
    }),
    endDate: z.string({
      error: issue =>
        issue.input === undefined
          ? intl.formatMessage(messages.errorRequiredEndDate)
          : "Invalid value",
    }),
    groups: z
      .array(
        z.object({
          name: z
            .string()
            .min(3, intl.formatMessage(messages.errorGroupNameMinLength, { min: 3 }))
            .optional(),
          hotelTickers: z
            .array(z.string())
            .min(1, intl.formatMessage(messages.errorAtLeastOneHotelTicker)),
        })
      )
      .min(1, intl.formatMessage(messages.errorAtLeastOneGroup)),
  });
}

type FunctionalityReportSchema = ReturnType<typeof makeSchema>;
export type FunctionalityReportFormInput = z.input<FunctionalityReportSchema>;
export type FunctionalityReportFormType = z.output<FunctionalityReportSchema>;

type FunctionalityReportProps = {
  section: string;
};

export function FunctionalityReport({ section }: FunctionalityReportProps) {
  const intl = useIntl();
  const { queryExecutorApiEndpoint } = useConfig();

  const methods = useForm<FunctionalityReportFormInput, unknown, FunctionalityReportFormType>({
    mode: "all",
    resolver: zodResolver(makeSchema(intl)),
    defaultValues: {
      functionalities: [],
      groups: [{ hotelTickers: [] }],
    },
  });

  const onSubmit: SubmitHandler<FunctionalityReportFormType> = useCallback(async () => {
    const data = await executeSuccessLogic(
      `${queryExecutorApiEndpoint}/query`,
      methods.getValues()
    );
    await jsonToXlsx(data, "functionality-report.xlsx");
  }, [methods, queryExecutorApiEndpoint]);

  const onError: SubmitErrorHandler<FunctionalityReportFormType> = useCallback(() => {
    console.error("form error", methods.formState.errors);
  }, [methods.formState.errors]);

  return (
    <AuthorizationVerifier section={section}>
      <ContentWrapper>
        <SectionHeading>
          <FormattedMessage {...messages.title} />
        </SectionHeading>
        <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
          <div className="flex flex-col items-start p-4">
            <Card className="w-full">
              <Card.Body className="flex gap-3 flex-col">
                <FunctionalityPeriod {...methods} />
                <FunctionalityFilter {...methods} />
                <FunctionalityGroup {...methods} />
                <Button type="submit" className="mt-3" disabled={methods.formState.isSubmitting}>
                  {methods.formState.isSubmitting ? (
                    <FormattedMessage {...messages.processing} />
                  ) : (
                    <FormattedMessage {...messages.export} />
                  )}
                </Button>
              </Card.Body>
            </Card>
          </div>
        </form>
      </ContentWrapper>
    </AuthorizationVerifier>
  );
}
